import React from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import pluralize from 'pluralize'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@src/components/base'
import { CoachAvatar } from '@src/components/Avatar/CoachAvatar'

interface CoachInfoCardContentProps {
  openBio: () => void
  fullName: string
  bio?: string | null
  photo?: string | null
  credentials?: string | null
  reviewsCount?: number | null
  averageRating?: number | null
  showPrimary?: boolean
  style?: StyleProp<ViewStyle>
}

export const CoachInfoCardContent = (props: CoachInfoCardContentProps) => {
  const styles = useStyleSheet(themedStyle)
  const {
    openBio,
    bio,
    fullName,
    photo,
    credentials,
    reviewsCount,
    averageRating,
    showPrimary = false,
    style,
  } = props

  return (
    <View style={[styles.container, style]}>
      <View style={styles.content}>
        <CoachAvatar photo={photo} style={styles.coachPhoto} showPrimary={showPrimary} />
        <View style={styles.textContainer}>
          <Text type="large" bold>
            {fullName}
          </Text>
          {!!credentials && (
            <Text type="small" style={styles.secondaryText}>
              {credentials}
            </Text>
          )}
          {!!averageRating && (
            <View style={styles.ratingContainer}>
              <Icon name="star" weight="fill" size={16} style={styles.starIcon} />
              <Text type="regular" bold>
                {averageRating.toFixed(1).replace('.', ',')}
              </Text>
              {!!reviewsCount && (
                <Text type="regular" style={styles.secondaryText}>
                  {' '}
                  ({pluralize('review', reviewsCount, true)})
                </Text>
              )}
            </View>
          )}
        </View>
        {!!bio && (
          <Button
            style={styles.button}
            accessibilityLabel="Show Bio"
            type="outline"
            size="small"
            onPress={openBio}
          >
            Show Bio
          </Button>
        )}
      </View>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    borderColor: 'theme.surface.base1',
    borderRadius: 16,
    borderWidth: 2,
    marginBottom: 16,
  },
  content: {
    padding: 16,
    flexDirection: 'row',
    gap: 8,
    borderColor: 'theme.surface.base1',
  },
  coachPhoto: {
    width: 48,
    height: 48,
    borderRadius: 32,
  },
  secondaryText: {
    color: 'theme.text.secondary',
  },
  textContainer: {
    flex: 1,
  },
  ratingContainer: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  starIcon: {
    color: 'theme.marketing.yellow',
    marginRight: 4,
  },
  spinner: {
    color: 'theme.surface.base',
  },
  button: {
    alignSelf: 'flex-start',
  },
})
